import { DOCUMENT } from '@angular/common';
import { effect, inject, Injectable, signal } from '@angular/core';

export type Theme = 'dark' | 'light';

const THEME_KEY = 'sso-backoffice.theme';

/**
 * Tema chiaro o scuro dell'interfaccia.
 *
 * Finché l'utente non sceglie, segue il sistema operativo — anche se cambia mentre la
 * pagina è aperta. Una volta scelto, la scelta si ricorda e il sistema non conta più:
 * chi passa al chiaro in un ufficio luminoso non vuole tornare allo scuro al tramonto.
 *
 * Il primo valore lo applica già `theme-init.js`, prima che parta Angular; qui lo si
 * rilegge dal documento per non decidere due volte.
 */
@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly root = inject(DOCUMENT).documentElement;
  private readonly media = window.matchMedia('(prefers-color-scheme: light)');
  private switchTimer: ReturnType<typeof setTimeout> | undefined;

  private readonly current = signal<Theme>(this.initial());
  readonly theme = this.current.asReadonly();

  constructor() {
    effect(() => this.root.setAttribute('data-theme', this.current()));

    this.media.addEventListener('change', (event) => {
      if (!this.stored()) {
        this.apply(event.matches ? 'light' : 'dark');
      }
    });
  }

  toggle(): void {
    const next: Theme = this.current() === 'dark' ? 'light' : 'dark';
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      // Senza storage il tema cambia comunque, solo non verrà ricordato.
    }
    this.apply(next);
  }

  /** Cambia tema con una breve dissolvenza dei colori (vedi `.theme-switching`). */
  private apply(theme: Theme): void {
    this.root.classList.add('theme-switching');
    clearTimeout(this.switchTimer);
    this.switchTimer = setTimeout(() => this.root.classList.remove('theme-switching'), 360);
    this.current.set(theme);
  }

  private initial(): Theme {
    const applied = this.root.getAttribute('data-theme');
    if (applied === 'light' || applied === 'dark') {
      return applied;
    }
    return this.stored() ?? (this.media.matches ? 'light' : 'dark');
  }

  private stored(): Theme | null {
    try {
      const value = localStorage.getItem(THEME_KEY);
      return value === 'light' || value === 'dark' ? value : null;
    } catch {
      return null;
    }
  }
}
